import React, { useEffect, useRef, useState } from "react";
import CkEditorField from "CommonComponent/CkEditorField/CkEditorField";
import { Row, Col, Form, InputGroup } from "react-bootstrap";
import { F } from "pages/AppraisalManagment/constants/legacyApiKeys";
import {
  APPRAISAL_MAX_RATING_LABEL,
  APPRAISAL_MAX_RATING_VALUE,
  formatMaxRatingLabel,
} from "pages/AppraisalManagment/utils/appraisalProgressSummary";

const getCollapsedMaxRatingLabel = () => APPRAISAL_MAX_RATING_LABEL;

const getQuestionHeadingLabel = (category, index) => {
  const t = category.title != null ? String(category.title).trim() : "";
  return t !== "" ? t : `Question ${index + 1}`;
};

const getQuestionLinkPillLabel = (category, index) => {
  const full = getQuestionHeadingLabel(category, index);
  if (full.length <= 44) return full;
  return `${full.slice(0, 44)}...`;
};

const applySortOrder = (items = []) =>
  items.map((item, index) => ({ ...item, sort_order: index + 1 }));

const SetupCategoryContent = ({
  setupRecordId,
  categories,
  setCategories,
  errors,
  clearFieldError,
  totals,
  showAddMore = true,
  isReadOnly = false,
}) => {
  const [collapsedRecordIds, setCollapsedRecordIds] = useState(() => new Set());
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const questionRefs = useRef({});
  const canReorder = !isReadOnly && categories.length > 1;

  useEffect(() => {
    if (!categories.length) {
      setCollapsedRecordIds(new Set());
      return;
    }
    const validIds = new Set(categories.map((cat) => cat.recordId));
    setCollapsedRecordIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [categories]);

  const handleAddMore = () => {
    if (isReadOnly) return;
    setCategories(
      applySortOrder([
        ...categories,
        {
          recordId: Date.now(),
          [F.setupPivotKey]: setupRecordId,
          title: "",
          rating: APPRAISAL_MAX_RATING_VALUE,
          weightage: null,
          description: "",
        },
      ])
    );
  };

  const handleDeleteCategory = (recordId) => {
    if (isReadOnly) return;
    setCategories(applySortOrder(categories.filter((cat) => cat.recordId !== recordId)));
  };

  const handleToggle = (recordId) => {
    setCollapsedRecordIds((prev) => {
      const next = new Set(prev);
      if (next.has(recordId)) next.delete(recordId);
      else next.add(recordId);
      return next;
    });
  };

  const handleQuestionLinkClick = (recordId) => {
    setCollapsedRecordIds((prev) => {
      const next = new Set(prev);
      next.delete(recordId);
      return next;
    });
    setTimeout(() => {
      questionRefs.current[recordId]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  };

  const handleChange = (index, field, value) => {
    if (isReadOnly) return;
    const updated = [...categories];
    updated[index][field] = value;
    setCategories(updated);

    // Remove error for this field if it exists
    if (errors[index]?.[field]) {
      clearFieldError(index, field);
    }
  };

  const handleWeightageChange = (index, rawValue) => {
    if (isReadOnly) return;
    if (rawValue === "" || rawValue === null || rawValue === undefined) {
      handleChange(index, "weightage", "");
      return;
    }
    const str = String(rawValue);
    if (/[.,eE+-]/.test(str)) return;
    const digitsOnly = str.replace(/\D/g, "");
    if (digitsOnly === "") {
      handleChange(index, "weightage", "");
      return;
    }
    const num = parseInt(digitsOnly, 10);
    if (!Number.isFinite(num)) return;
    handleChange(index, "weightage", String(Math.min(100, num)));
  };

  const resetDragState = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleDragStart = (event, index) => {
    if (!canReorder) return;
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
    setDragIndex(index);
  };

  const handleDragOver = (event, index) => {
    if (!canReorder || dragIndex === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (dropIndex !== index) setDropIndex(index);
  };

  const handleDrop = (event, toIndex) => {
    if (!canReorder) return;
    event.preventDefault();
    event.stopPropagation();
    const fromRaw = event.dataTransfer.getData("text/plain");
    const fromIndex = Number(fromRaw);
    if (!Number.isInteger(fromIndex) || fromIndex < 0 || fromIndex >= categories.length) {
      resetDragState();
      return;
    }
    if (fromIndex === toIndex) {
      resetDragState();
      return;
    }
    const next = [...categories];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setCategories(applySortOrder(next));
    resetDragState();
  };

  const isQuestionExpanded = (recordId) => !collapsedRecordIds.has(recordId);

  return (
    <div className="p-4">
      <div className="setup-content-div">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="font-13">
            Total Weightage: <strong>{totals?.total ?? 0}</strong>/100
            <span className={`ms-3 ${totals?.remaining === 0 ? "text-success" : totals?.remaining < 0 ? "text-danger" : "text-warning"}`}>
              Remaining: <strong>{totals?.remaining ?? 100}</strong>
            </span>
          </div>
          {errors?._totalWeightage && (
            <div className="text-danger font-13">{errors._totalWeightage}</div>
          )}
        </div>
        <div className="question-links-wrap mb-3">
          {categories.map((category, index) => (
            <button
              key={`q-link-${category.recordId}`}
              type="button"
              className={`question-link-btn ${isQuestionExpanded(category.recordId) ? "active" : ""}`}
              onClick={() => handleQuestionLinkClick(category.recordId)}
            >
              {getQuestionLinkPillLabel(category, index)}
            </button>
          ))}
        </div>
        {categories.map((category, index) => {
          const isExpanded = isQuestionExpanded(category.recordId);
          const barLabel = getQuestionHeadingLabel(category, index);
          const headerId = `setup-q-h-${category.recordId}`;
          const regionId = `setup-q-body-${category.recordId}`;
          const isDragging = dragIndex === index;
          const isDropTarget = dropIndex === index && dragIndex !== null && dragIndex !== index;

          return (
          <div
            key={category.recordId}
            ref={(el) => {
              questionRefs.current[category.recordId] = el;
            }}
            className={`mb-4 question-card${isDragging ? " is-dragging" : ""}${isDropTarget ? " is-drop-target" : ""}`}
            onDragOver={(event) => handleDragOver(event, index)}
            onDrop={(event) => handleDrop(event, index)}
          >
            <div className="setup-question-header-row">
              {canReorder && (
                <span
                  className="setup-question-drag-handle"
                  draggable
                  role="button"
                  tabIndex={0}
                  aria-label={`Reorder ${barLabel}`}
                  title="Drag to reorder"
                  onDragStart={(event) => handleDragStart(event, index)}
                  onDragEnd={resetDragState}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                    event.preventDefault();
                    const toIndex = event.key === "ArrowUp" ? index - 1 : index + 1;
                    if (toIndex < 0 || toIndex >= categories.length) return;
                    const next = [...categories];
                    const [moved] = next.splice(index, 1);
                    next.splice(toIndex, 0, moved);
                    setCategories(applySortOrder(next));
                  }}
                >
                  <svg width="14" height="18" viewBox="0 0 14 18" aria-hidden="true" focusable="false">
                    <circle cx="4" cy="3" r="1.5" fill="currentColor" />
                    <circle cx="10" cy="3" r="1.5" fill="currentColor" />
                    <circle cx="4" cy="9" r="1.5" fill="currentColor" />
                    <circle cx="10" cy="9" r="1.5" fill="currentColor" />
                    <circle cx="4" cy="15" r="1.5" fill="currentColor" />
                    <circle cx="10" cy="15" r="1.5" fill="currentColor" />
                  </svg>
                </span>
              )}
            <button
              type="button"
              className="setup-question-header-bar d-flex align-items-center justify-content-between"
              id={headerId}
              aria-expanded={isExpanded}
              aria-controls={regionId}
              onClick={() => handleToggle(category.recordId)}
            >
              <div className="setup-question-header-title-block d-flex align-items-center flex-grow-1 flex-wrap gap-2 pe-2 text-start">
                <span className="font-13 font-weight-s-bold heading-color">{barLabel}</span>
                {!isExpanded && (
                  <>
                    <span className="setup-question-header-meta">
                      <span className="setup-question-header-meta-label">Weightage</span>
                      {category.weightage != null && String(category.weightage).trim() !== ""
                        ? String(category.weightage).trim()
                        : "—"}
                    </span>
                    <span className="setup-question-header-meta">
                      <span className="setup-question-header-meta-label">Max Rating</span>
                      {getCollapsedMaxRatingLabel(category)}
                    </span>
                  </>
                )}
              </div>
              <span className="question-toggle-btn flex-shrink-0" aria-hidden="true">
                {isExpanded ? "Collapse" : "Expand"}
              </span>
            </button>
            </div>

            <div
              id={regionId}
              role="region"
              aria-labelledby={headerId}
              className={`question-card-body ${isExpanded ? "expanded" : "collapsed"}`}
            >
              <div className="question-card-body-inner">
            <Row className="mb-3 mt-2">
              <Col lg={8}>
                <Form.Group className="mb-3 mb-lg-0">
                  <Form.Label className="label-input">Label</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Category name"
                    className="input-field compact-field"
                    value={category.title}
                    onChange={(e) => handleChange(index, "title", e.target.value)}
                    isInvalid={!!errors[index]?.title}
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[index]?.title?.[0]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-0">
                  <Form.Label className="label-input">Max Rating</Form.Label>
                  <Form.Control
                    type="text"
                    className="input-field compact-field"
                    value={formatMaxRatingLabel(category.rating ?? APPRAISAL_MAX_RATING_VALUE)}
                    readOnly
                    disabled={isReadOnly}
                  />
                  {errors[index]?.rating && (
                    <div className="text-danger mt-1 font-12">
                      {errors[index].rating[0]}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={4}>
                <Form.Group className="mb-0">
                  <Form.Label className="label-input">Question Weightage (%)</Form.Label>
                  <InputGroup>
                  <Form.Control
                    type="number"
                      min="1"
                      max="100"
                      step="1"
                      placeholder="Enter weightage"
                      className="input-field compact-field"
                      value={category.weightage ?? ""}
                      onKeyDown={(e) => {
                        if ([".", ",", "e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                      }}
                      onChange={(e) => handleWeightageChange(index, e.target.value)}
                      isInvalid={!!errors[index]?.weightage}
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </InputGroup>
                  {errors[index]?.weightage && (
                    <div className="text-danger mt-1 font-12">
                      {errors[index]?.weightage?.[0]}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <CkEditorField
                    data={category.description}
                    readOnly={isReadOnly}
                    onChange={(html) => handleChange(index, "description", html)}
                  />
                  {errors[index]?.description && (
                    <div className="text-danger mt-1 font-12">
                      {errors[index].description[0]}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {!isReadOnly && categories.length > 1 && (
              <div className="text-end">
                <img
                  src="/assets/images/delete-icon.svg"
                  alt="delete"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleDeleteCategory(category.recordId)}
                />
              </div>
            )}
              </div>
            </div>
          </div>
          );
        })}

        {showAddMore && !isReadOnly && (
          <button
            type="button"
            className="font-weight-s-bold heading-color border-0 bg-transparent"
            style={{ fontSize: "15px", padding: "6px 10px" }}
            onClick={handleAddMore}
          >
            + Add more
          </button>
        )}
      </div>
    </div>
  );
};

export default SetupCategoryContent;
